'use client'

import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { PICKED_FILL } from '@/lib/surface'
import { META, MICRO } from '@/lib/typography'
import { cn } from '@/lib/utils'

/**
 * Date + time picker for the consultation booking.
 *
 * WHAT HAPPENS TO THE SLOT IS THE CALLER'S BUSINESS, and the two answers are
 * genuinely different. `/pl/kontakt` books the free discovery call outright —
 * the hour is written into `booking` as the form is sent — while `/pl/strategia`
 * and `/pl/fotograf-operator` hand the choice to the checkout, where nothing is
 * held until an order is placed. This component reserves nothing either way; it
 * offers what `openDays`/`slots` say is free and reports what was picked.
 *
 * `reservationNote` is what tells those two apart on screen, and it is a prop
 * for exactly that reason: the default sentence promises only a confirmation,
 * which would be a lie on the page that reserves.
 *
 * A month grid rather than a day strip: the horizon is six months, which is
 * ~130 business days — far too many to scroll through horizontally.
 *
 * "Today" is resolved in an effect, not during render: `new Date()` differs
 * between the server pass and the client one, which would trip hydration.
 */

/**
 * The default start times: an hourly meeting, 09:00 to 16:00.
 *
 * Right for a consultation and for a studio hour, and wrong for a shoot — a
 * photographer's day opens at 08:00 and how late it can start depends on how
 * long the plan runs. That is why `slots` is a prop: the production page passes
 * its own list, and the two callers that book by the hour keep this one by
 * saying nothing.
 */
const SLOTS = ['09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00']
const MONTHS_AHEAD = 6

export type BookingSlot = { date: string; time: string } | null

type Props = {
  value: BookingSlot
  onChange: (slot: BookingSlot) => void
  /**
   * Start times to offer, when the default set is not the right one.
   *
   * The caller owns the list because it owns the reason — a plan's length, a
   * room's opening hours — and this component has no way to know either. It
   * also owns keeping `value` inside it: a time that is no longer offered goes
   * on rendering as chosen here, because the selected state is `value.time`
   * matching, not membership of this array.
   *
   * A FUNCTION when the answer differs by day, which is the studio's case: the
   * office keeps different hours on a Friday and a Saturday, and the last start
   * is closing time minus the block. The day the reader has picked lives in
   * this component's own state — `value` is only set once a TIME is chosen too
   * — so a caller that needs it has to be handed it rather than guess.
   */
  slots?: readonly string[] | ((dayISO: string) => readonly string[])
  /**
   * The only days that may be picked, `YYYY-MM-DD`. Omit to keep the weekday
   * rule below.
   *
   * A shoot is not bookable on "any weekday" — it is bookable on the days a
   * photographer actually put in their calendar, and spec §6 lets that include
   * a Saturday. Passing the list moves that decision to whoever knows it,
   * which is never this component.
   *
   * An EMPTY array is a real answer — "nobody has room in the horizon" — and is
   * rendered as such. `undefined` is the absence of the question.
   */
  openDays?: readonly string[]
  /**
   * The line under the month grid, when the default one would be wrong.
   *
   * The default explains a capacity calendar — "terminy z kalendarza ekipy" —
   * which is what `openDays` means for a shoot. For the studio the same prop
   * carries OPENING HOURS, and nobody declared them; the room is simply open.
   */
  note?: string
  /**
   * The line under the CHOSEN slot, when the default one would be wrong.
   *
   * The default is „Potwierdzimy go mailowo, termin nie jest jeszcze
   * zarezerwowany", which is true of the two callers that hand the slot on to
   * the checkout — nothing is held until an order is placed — and is a LIE on
   * `/pl/kontakt`, where the free call is written into the calendar the moment
   * the form is sent. A picker that reserves and a picker that asks look the
   * same; only the sentence tells them apart, so it is a prop rather than a
   * constant.
   */
  reservationNote?: string
  /**
   * Whether to print „Wybierz dzień" over the month.
   *
   * On by default, because on `/pl/kontakt` and `/pl/strategia` it is the only
   * thing telling a reader what the grid is for. Off where a step heading has
   * just asked the same question — `/pl/fotograf-operator` writes „3 · Kiedy?"
   * directly above this panel, so the label is the question asked twice.
   *
   * It is 26px including its gap, and that is why it is a prop rather than a
   * judgement call: those 26px are the difference between the month fitting a
   * column on an 820px window and the panel scrolling. A label nobody needs is
   * the right place to find them.
   */
  showDayLabel?: boolean
}

const monthFormatter = new Intl.DateTimeFormat('pl-PL', { month: 'long', year: 'numeric' })
const longFormatter = new Intl.DateTimeFormat('pl-PL', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
})

/** Monday-first, matching the Polish convention. */
const WEEKDAYS = ['pon', 'wt', 'śr', 'czw', 'pt', 'sob', 'nd']

/**
 * Local YYYY-MM-DD. Deliberately not `toISOString()`: that converts to UTC, so
 * a local midnight in Poland (UTC+1/+2) lands on the previous calendar day and
 * the key would disagree with the date the user sees.
 */
function toKey(date: Date): string {
  const m = `${date.getMonth() + 1}`.padStart(2, '0')
  const d = `${date.getDate()}`.padStart(2, '0')
  return `${date.getFullYear()}-${m}-${d}`
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

function addMonths(date: Date, count: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + count, 1)
}

/** Cells for a month grid, padded with nulls so the 1st sits under its weekday. */
function monthCells(month: Date): (Date | null)[] {
  const first = startOfMonth(month)
  // getDay() is Sunday-based; shift so Monday is 0.
  const lead = (first.getDay() + 6) % 7
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate()

  const cells: (Date | null)[] = Array(lead).fill(null)
  for (let d = 1; d <= daysInMonth; d += 1) {
    cells.push(new Date(month.getFullYear(), month.getMonth(), d))
  }
  return cells
}

export function ConsultationCalendar({
  value,
  onChange,
  slots = SLOTS,
  openDays,
  note,
  reservationNote,
  showDayLabel = true,
}: Props) {
  /** The times this particular day offers — see the `slots` prop. */
  const slotsFor = (dayISO: string): readonly string[] =>
    typeof slots === 'function' ? slots(dayISO) : slots
  // null until the client resolves "today" — see the hydration note above.
  const [today, setToday] = useState<Date | null>(null)
  const [viewMonth, setViewMonth] = useState<Date | null>(null)
  // Day lives here rather than in `value`: a BookingSlot is only emitted once a
  // time is picked too, so consumers never receive a half-made selection.
  const [activeDay, setActiveDay] = useState<string | null>(null)

  /**
   * Whether the month grid is open once a day has been chosen.
   *
   * THE GRID FOLDS AWAY AFTER A PICK. Owner: *„zróbmy coś takiego, żeby nie
   * było trzeba przesuwać tutaj góra-dół"*. Picking a day adds a row of hour
   * chips, and in a panel sized to a column — `/pl/fotograf-operator`, `/pl/strategia`
   * — day grid plus hours is ~140px more than there is room for, so the reader
   * had to scroll inside the panel to reach the control they had just asked
   * for. Scrolling them there automatically was the first attempt and it is the
   * wrong fix: it moves a box the reader did not know could move.
   *
   * A month grid that has done its job is 200px of answered question. Folding
   * it to one line is what makes the hours fit with nothing to scroll — and it
   * is the same shape every booking flow settles on for the same reason.
   *
   * State rather than derived from `activeDay`, because "a day is chosen" and
   * "the reader wants the grid back" are different facts: `Zmień` reopens the
   * grid WITHOUT clearing the day, so nothing is lost by looking.
   */
  const [browsing, setBrowsing] = useState(false)

  useEffect(() => {
    const now = new Date()
    now.setHours(0, 0, 0, 0)
    setToday(now)
    setViewMonth(startOfMonth(now))
  }, [])

  const bounds = useMemo(() => {
    if (!today) return null
    // Same-day consultations aren't offered, so the window opens tomorrow.
    const min = new Date(today)
    min.setDate(min.getDate() + 1)
    const max = new Date(today)
    max.setMonth(max.getMonth() + MONTHS_AHEAD)
    return { min, max }
  }, [today])

  const cells = useMemo(() => (viewMonth ? monthCells(viewMonth) : []), [viewMonth])
  // A Set because `isBookable` runs once per cell per render — 42 lookups a
  // month, and the caller's list can be a couple of hundred days long.
  const declared = useMemo(() => (openDays ? new Set(openDays) : null), [openDays])

  if (!viewMonth || !bounds) {
    return (
      <div className="h-80 animate-pulse rounded-2xl border border-foreground/10 bg-foreground/[0.03]" />
    )
  }

  const isBookable = (day: Date) => {
    if (day < bounds.min || day > bounds.max) return false
    // A declared list REPLACES the weekday rule rather than narrowing it. That
    // is the whole point: a Saturday somebody put in their own calendar has to
    // come through, and an `&&` with the weekend test would silently drop it.
    if (declared) return declared.has(toKey(day))
    const weekday = day.getDay()
    return weekday !== 0 && weekday !== 6
  }

  const canGoBack = startOfMonth(bounds.min) < viewMonth
  const canGoForward = addMonths(viewMonth, 1) <= startOfMonth(bounds.max)

  // Parsed from the key rather than looked up in `cells`: the chosen day may
  // live in a month the user has since navigated away from.
  const activeLabel = activeDay ? longFormatter.format(new Date(`${activeDay}T00:00:00`)) : null

  return (
    // Capped: the day cells are square, so on a full-width panel they grow into
    // huge tiles and the month needs a scroll to take in at a glance. Centred,
    // because the cap otherwise leaves the month hugging the left edge of a
    // full-width card with dead space beside it.
    <div className="mx-auto flex w-full max-w-[30rem] flex-col gap-[clamp(0.75rem,2.1svh,1.25rem)]">
      {/* ── THE DAY, ONCE IT IS CHOSEN ─────────────────────────────
        One line in place of a month. See `browsing` above for why: the grid
        and the hours together do not fit a panel sized to a column, and the
        grid is the half that has been answered. `Zmień` brings it back without
        clearing the day. */}
      {activeDay && !browsing ? (
        <div className="flex items-center justify-between gap-3 rounded-2xl border border-foreground/12 bg-foreground/[0.04] px-4 py-2.5">
          <span className="min-w-0">
            <span className={cn(META, 'block font-medium text-muted-foreground')}>Dzień</span>
            <span className="block truncate text-[0.95rem] font-semibold text-foreground first-letter:uppercase">
              {activeLabel}
            </span>
          </span>
          <button
            type="button"
            onClick={() => setBrowsing(true)}
            className="shrink-0 rounded-full border border-foreground/15 px-3 py-1.5 text-[0.8rem] font-semibold text-foreground/70 transition hover:border-foreground/40 hover:text-foreground"
          >
            Zmień
          </button>
        </div>
      ) : (
        <>
          {/* ── MONTH NAV ──────────────────────────────────────────────
          Two rows, not one. Everything used to share a line: the "Wybierz
          dzień" label, both arrows, and the month name under a `min-w-[9rem]`
          that stopped the arrows jittering as the name changed length. Added
          up, that row could not go below 281px — so on a 375px phone the grid
          was clipped by 4px and on a 320px one by 59px, taking most of Sunday
          with it. Giving the month name `flex-1` removes the floor entirely;
          it is centred between the arrows, so it still cannot shift them. */}
          <div className="flex flex-col gap-2">
            {showDayLabel ? (
              <p className={cn(META, 'font-medium text-muted-foreground')}>Wybierz dzień</p>
            ) : null}
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => canGoBack && setViewMonth(addMonths(viewMonth, -1))}
                disabled={!canGoBack}
                aria-label="Poprzedni miesiąc"
                className="flex size-9 shrink-0 items-center justify-center rounded-full border border-foreground/15 text-foreground/70 transition hover:border-foreground/40 hover:text-foreground disabled:pointer-events-none disabled:opacity-30"
              >
                <ChevronLeft size={16} aria-hidden="true" />
              </button>
              <span
                aria-live="polite"
                className="flex-1 truncate text-center text-[0.95rem] font-semibold capitalize text-foreground"
              >
                {monthFormatter.format(viewMonth)}
              </span>
              <button
                type="button"
                onClick={() => canGoForward && setViewMonth(addMonths(viewMonth, 1))}
                disabled={!canGoForward}
                aria-label="Następny miesiąc"
                className="flex size-9 shrink-0 items-center justify-center rounded-full border border-foreground/15 text-foreground/70 transition hover:border-foreground/40 hover:text-foreground disabled:pointer-events-none disabled:opacity-30"
              >
                <ChevronRight size={16} aria-hidden="true" />
              </button>
            </div>
          </div>

          {/* ── MONTH GRID ─────────────────────────────────────── */}
          <div>
            <div className="grid grid-cols-7 gap-1 pb-1.5">
              {WEEKDAYS.map((label) => (
                <span
                  key={label}
                  className={cn(
                    MICRO,
                    'text-center font-semibold tracking-normal text-muted-foreground uppercase',
                  )}
                >
                  {label}
                </span>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {cells.map((day, i) => {
                if (!day) return <span key={`pad-${i}`} aria-hidden="true" />

                const key = toKey(day)
                const bookable = isBookable(day)
                const isSelected = activeDay === key
                // Somewhere to orient from. A month grid with no "you are here" is
                // a grid of numbers.
                const isToday = key === toKey(new Date())

                return (
                  <button
                    key={key}
                    type="button"
                    disabled={!bookable}
                    onClick={() => {
                      setActiveDay(key)
                      // Fold the month away: the question it asks has just been
                      // answered, and the hours need the room it is holding.
                      setBrowsing(false)
                      // A new day invalidates the old time — clear it so the summary
                      // can never show a half-changed selection.
                      if (value?.date !== key) onChange(null)
                    }}
                    aria-pressed={isSelected}
                    className={cn(
                      // `min-h-11` (44px) rather than `aspect-square`: seven square
                      // cells plus gaps cannot reach a 44px tap target inside a
                      // 375px screen — they land at ~40px wide. Height is the axis
                      // a thumb misses on, so it is the one held to the minimum.
                      'relative min-h-11 rounded-xl border text-[0.8rem] font-semibold transition',
                      // THREE states that have to read as three, not as two and a
                      // shade. A free day used to differ from a taken one by the
                      // presence of a hairline, which is nothing at a glance — the
                      // whole month looked equally available, and the only way to
                      // find out was to click.
                      isSelected
                        ? PICKED_FILL
                        : bookable
                          ? 'border-foreground/12 bg-foreground/[0.06] text-foreground hover:border-foreground/40 hover:bg-foreground/[0.12]'
                          : 'cursor-not-allowed border-transparent text-foreground/20',
                      isToday && !isSelected ? 'ring-1 ring-foreground/30 ring-inset' : null,
                    )}
                  >
                    {day.getDate()}
                    {/* A free day says so before it is clicked. Under the number so
                      it never crowds it, and hidden on the picked day, where the
                      fill has already answered the question. */}
                    {bookable && !isSelected ? (
                      <span
                        aria-hidden="true"
                        className="absolute inset-x-0 bottom-1.5 mx-auto block size-1 rounded-full bg-[image:var(--flou-gradient)]"
                      />
                    ) : null}
                  </button>
                )
              })}
            </div>

            <p className="mt-2.5 text-[0.75rem] text-muted-foreground">
              {note
                ? note
                : !declared
                  ? `Terminy w dni robocze, do ${String(MONTHS_AHEAD)} miesięcy do przodu.`
                  : declared.size === 0
                    ? 'Brak wolnych terminów — napisz do nas, dogramy datę indywidualnie.'
                    : 'Terminy z kalendarza ekipy, weekendy włącznie, jeśli ktoś je zadeklarował.'}
            </p>
          </div>
        </>
      )}

      {/* ── SLOTS ──────────────────────────────────────────── */}
      {/* Hidden while the month is open. `Zmień` means the day is being
          re-chosen, so the hours on screen belong to a day that is on its way
          out — and showing both is the one state that put the scroll back
          (measured: 75px over). One question at a time, and the panel never
          moves. */}
      {activeDay && !browsing ? (
        <div>
          <p className="mb-2 text-[0.8rem] font-medium text-muted-foreground">
            Wybierz godzinę {activeLabel ? `${activeLabel}` : null}
          </p>
          {slotsFor(activeDay).length === 0 ? (
            <p className="text-[0.8rem] text-muted-foreground">
              Tego dnia nie zmieścimy już tak długiej rezerwacji — wybierz inny dzień albo krótszy
              blok.
            </p>
          ) : null}
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {slotsFor(activeDay).map((time) => {
              const isActive = value?.date === activeDay && value?.time === time
              return (
                <button
                  key={time}
                  type="button"
                  onClick={() => onChange({ date: activeDay, time })}
                  aria-pressed={isActive}
                  className={cn(
                    'rounded-2xl border px-3 py-2.5 text-sm font-semibold transition',
                    isActive
                      ? PICKED_FILL
                      : 'border-foreground/12 bg-foreground/[0.06] text-foreground/85 hover:border-foreground/35 hover:bg-foreground/[0.12]',
                  )}
                >
                  {time}
                </button>
              )
            })}
          </div>
        </div>
      ) : null}

      {value && activeLabel ? (
        <p className="rounded-2xl border border-foreground/10 bg-foreground/[0.04] px-4 py-3 text-[0.8rem] text-foreground/80">
          Preferowany termin:{' '}
          <span className="font-semibold text-foreground">
            {activeLabel}, {value.time}
          </span>
          <span className="mt-1 block text-[0.75rem] text-muted-foreground">
            {reservationNote ?? 'Potwierdzimy go mailowo, termin nie jest jeszcze zarezerwowany.'}
          </span>
        </p>
      ) : null}
    </div>
  )
}
